import { Routes , Route } from 'react-router-dom';
import Error from './pages/Error'
import Home from './pages/Home'
import Search from './pages/Search'
import Login from './pages/Login'
import LoginCode from './pages/LoginCode'
import Profile from './pages/Profile'
import Loading from './components/Loading'
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { loginWithAccessToken, loginWithCode, loginWithServerToken, loginWithToken } from './features/users/login.js';
import Help from './pages/Help';
import Language from './pages/Language';
import Registration from './pages/Registration';
import Window from './windows/Window';
import ResetPassword from './pages/ResetPassword';
import LoadingUserData from './components/LoadingUserData';
import { setLevelId } from './features/search/searchSlice.js';

const AUTH_QUERY_PARAMS = [
  "accessToken",
  "ssoId",
  "token",
  "clientToken",
  "clientCode",
  "returnUrl",
];

function App() {

  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const activeUser = useSelector((state) => state.user.activeUser);
  const selectedLevelId = useSelector((state) => state.search.levelId);
  const lastAutoAppliedLevelKey = useRef(null);
  const processedAuthQueryRef = useRef(null);
  const searchParamsKey = searchParams.toString();

  const authParams = useMemo(() => {
    const parsedSearchParams = new URLSearchParams(searchParamsKey);
    return {
      accessToken: parsedSearchParams.get("accessToken"),
      ssoId: parsedSearchParams.get("ssoId"),
      token: parsedSearchParams.get("token"),
      clientToken: parsedSearchParams.get("clientToken"),
      clientCode: parsedSearchParams.get("clientCode"),
      returnUrl: parsedSearchParams.get("returnUrl"),
    };
  }, [searchParamsKey]);

  const stripAuthParamsFromUrl = useCallback(() => {
    const nextParams = new URLSearchParams(searchParamsKey);
    let hasChanges = false;

    for (const key of AUTH_QUERY_PARAMS) {
      if (nextParams.has(key)) {
        hasChanges = true;
        nextParams.delete(key);
      }
    }

    if (hasChanges) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [searchParamsKey, setSearchParams]);

  useEffect(() => {
      if (!dispatch) return;
      const accessToken = authParams.accessToken;
      if (accessToken)
      {
        if (processedAuthQueryRef.current === searchParamsKey) return;
        processedAuthQueryRef.current = searchParamsKey;
        stripAuthParamsFromUrl();
        loginWithAccessToken(dispatch, accessToken);
        return;
      }
      const ssoId = authParams.ssoId;
      if (ssoId)
      {
        if (processedAuthQueryRef.current === searchParamsKey) return;
        processedAuthQueryRef.current = searchParamsKey;
        const token = authParams.token;
        const clientToken = authParams.clientToken;
        const clientCode = authParams.clientCode;
        const returnUrlRaw = authParams.returnUrl;

        stripAuthParamsFromUrl();

        const isStaging = window.location.href.includes("app-staging.");

        let returnUrl = !isStaging ? "https://www.bookrclass.com/?platform=web" : "https://www.bookrclass.com/?platform=webstg";

        if (returnUrlRaw !== null && returnUrlRaw !== "") {
          if (returnUrlRaw.includes("/?")){
            returnUrl = returnUrlRaw + "&platform=web";
          } else if (returnUrlRaw.substring(returnUrlRaw.length - 1) === "/") {
            returnUrl = returnUrlRaw + "?platform=web";
          } else {
            returnUrl = returnUrlRaw + "/?platform=web";
          }
        }

        if (token)
        {
          loginWithServerToken(dispatch, { token: token, ssoId: ssoId });
          console.log("loginWithServerToken CALLED!");          
        } else if (clientToken)
        {
          loginWithToken({ token: clientToken, ssoId: ssoId, returnUrl: returnUrl });
        } else if (clientCode) {
          loginWithCode({ code: clientCode, ssoId: ssoId, returnUrl: returnUrl });
        }
      }
  }, [authParams, dispatch, searchParamsKey, stripAuthParamsFromUrl]);

  useEffect(() => {
      const userId = activeUser?.id;
      if (!userId) {
        lastAutoAppliedLevelKey.current = null;
        if (selectedLevelId !== 0) {
          dispatch(setLevelId(0));
        }
        return;
      }

      const hasLevelInUrl = !!(searchParams.get("level") || searchParams.get("levelId"));
      const userLevelId = activeUser?.type === "child" ? activeUser?.level?.id : null;
      if (!userLevelId || hasLevelInUrl) return;

      // Apply whenever login user/level changes (including relogin), without overriding URL-driven filters.
      const levelKey = `${userId}:${userLevelId}`;
      if (lastAutoAppliedLevelKey.current !== levelKey) {
        dispatch(setLevelId(userLevelId));
        lastAutoAppliedLevelKey.current = levelKey;
      }
  }, [activeUser, selectedLevelId, searchParams, dispatch]);

  return (
    <div>
      <LoadingUserData />
      <Loading/>
      <Routes >
        <Route exact path="/" element={<Home/>}  />
        <Route exact path="/search" element={<Search/>}  />
        <Route exact path="/login" element={<Login/>}  />
        <Route exact path="/code-sign-in" element={<LoginCode/>}  />
        <Route exact path="/reset-password" element={<ResetPassword/>}  />
        <Route exact path="/profile" element={<Profile/>} />
        <Route exact path="/help" element={<Help/>} />
        <Route exact path="/language" element={<Language/>} />
        <Route exact path="/register" element={<Registration/>} />
        <Route path="*" element={<Error/>} />
      </Routes >
      <Window />
    </div>
  );
}

export default App;
